<script lang="ts" setup>
import { Article } from '~/types';
import { useArticle } from '~/composables/useArticle';
import Container from '~/components/Container.vue';
import Image from '~/components/ui/Image.vue';

interface Props {
  article: Article.Model;
}

const props = defineProps<Props>();

const { articleDate, authorName, featuredImageSrcSet, shortDescription, showAuthor, title } = useArticle(props.article);
</script>

<template>
  <section class="article-head">
    <Container class="article-head__container">
      <h1 v-if="title" class="article-head__title">
        {{ title }}
      </h1>
      <div class="article-head__text-box">
        <div class="article-head__meta">
          <time v-if="articleDate" class="article-head__date">{{ articleDate }}</time>
          <p v-if="showAuthor && authorName" class="article-head__author"><span>by</span> {{ authorName }}</p>
        </div>
        <p v-if="shortDescription" class="article-head__description">{{ shortDescription }}</p>
      </div>
    </Container>
    <Image v-if="featuredImageSrcSet" :image="featuredImageSrcSet" class="article-head__image" />
  </section>
</template>

<style lang="scss" scoped>
.article-head {
  @apply mt-10 flex flex-col gap-8;
  @apply md:mt-16;
  @apply xl:mt-20 xl:gap-20;
  @apply 2xl:mt-28;

  &__container {
    @apply flex flex-col gap-6;
    @apply xl:flex-row xl:items-end;
  }

  &__image {
    @apply w-full max-w-[1920px] mx-auto rounded-t-10 overflow-hidden max-h-[800px] object-cover object-center;
    @apply xl:rounded-t-30;
  }

  &__title {
    @apply font-title text-[40px] leading-[1.1];
    @apply lg:text-[50px];
    @apply xl:w-1/2 xl:text-[70px];
    @apply 3xl:text-[90px];
  }

  &__text-box {
    @apply flex flex-col gap-5;
    @apply xl:w-1/2 xl:pl-10 xl:gap-10;
    @apply 2xl:pl-20;
  }

  &__date {
    @apply font-semibold text-[16px];
    @apply xl:text-[18px];
  }
}
</style>
